import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Styles/Events.module.css';
import { getAllEvents, cleanupE2ETest } from '../../Services/arndAPI';
import Loader from '../../Components/Loader/Loader';
import ConfirmModal from '../../Components/ConfirmModal/ConfirmModal';

export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [eventToDelete, setEventToDelete] = useState(null); // holds the eventData object
  const [filters, setFilters] = useState({
    eventId: '',
    name: '',
    location: '',
    path: '',
    date: '',
    eventType: '',
    order: 'desc',
  });

  useEffect(() => {
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const data = await getAllEvents(filters);
      setEvents(data);
    } catch {
      setStatus('Error loading events');
    } finally {
      setLoading(false);
    }
  };

  // Delete is only offered for leftover E2E test events. The backend cleanup
  // endpoint independently re-validates this prefix, so real events are safe.
  const isE2EEvent = (eventData) => eventData?.name?.startsWith('E2E-test-');

  const handleRequestDelete = (eventData) => setEventToDelete(eventData);

  const confirmDelete = async () => {
    setStatus('Deleting event...');
    try {
      await cleanupE2ETest(eventToDelete.id);
      setStatus('Event deleted.');
      fetchEvents();
    } catch (err) {
      setStatus(err.message || 'Error deleting event.');
    } finally {
      setEventToDelete(null);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchEvents();
  };

  const navigate = useNavigate();
  return (
    <div className={styles.container}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h2>Manage Events</h2>
        <button
          className={styles.createBtn}
          onClick={() => navigate('/events/create')}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" /></svg>
            Create New Event
          </span>
        </button>
      </div>
      <form className={styles.filters} onSubmit={handleSearch}>
        <input
          type="text"
          name="eventId"
          placeholder="Event ID"
          value={filters.eventId}
          onChange={handleFilterChange}
        />
        <input
          type="text"
          name="name"
          placeholder="Event Name"
          value={filters.name}
          onChange={handleFilterChange}
        />
        <input
          type="date"
          name="date"
          placeholder="Date"
          value={filters.date}
          onChange={handleFilterChange}
          style={{ minWidth: 140 }}
        />
        <input
          type="text"
          name="location"
          placeholder="Location"
          value={filters.location}
          onChange={handleFilterChange}
        />
        <input
          type="text"
          name="path"
          placeholder="Path"
          value={filters.path}
          onChange={handleFilterChange}
        />
        <input
          type="text"
          name="eventType"
          placeholder="Event Type"
          value={filters.eventType}
          onChange={handleFilterChange}
        />
        <select
          name="order"
          value={filters.order}
          onChange={e => setFilters(prev => ({ ...prev, order: e.target.value }))}
          style={{ marginLeft: '1rem', minWidth: 120 }}
        >
          <option value="desc">Newest First</option>
          <option value="asc">Oldest First</option>
        </select>
        <button type="submit">Search</button>
      </form>

      {loading ? (
        <Loader />
      ) : (
        <div className={styles.cardsContainer}>
          {events.map((event) => {
            const { eventData } = event;
            return (
              <div key={eventData.id} className={styles.card}>
                {eventData.eventBanner && (
                  <img
                    src={eventData.eventBanner}
                    alt="Event Banner"
                    style={{ width: '100%', height: '160px', objectFit: 'cover', borderRadius: '8px', marginBottom: '1rem' }}
                  />
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                  {eventData.iconUrl && (
                    <img
                      src={eventData.iconUrl}
                      alt="Event Icon"
                      style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '50%' }}
                    />
                  )}
                  <h3 style={{ margin: 0 }}>{eventData.name}</h3>
                </div>
                <p><strong>Type:</strong> {eventData.eventType}</p>
                <p style={{ cursor: 'pointer' }}
                  onClick={() => { navigator.clipboard.writeText(eventData.id) }}
                >
                  <strong>ID:</strong> Click to Copy&nbsp;
                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><rect x="9" y="9" width="13" height="13" rx="2" /><rect x="2" y="2" width="13" height="13" rx="2" /></svg>
                </p>
                <p><strong>Date:</strong> {eventData.date ? eventData.date.substring(0, 10) : ''}</p>
                <p><strong>Location:</strong> {eventData.location}</p>
                <p><strong>Path:</strong> {eventData.path}</p>
                <p><strong>Photographer:</strong> {eventData.User[0]?.fullName || 'Unassigned'}</p>
                <p><strong>Deleted:</strong> {eventData.deletedAt ? 'Yes' : 'No'}</p>
                <button
                  className={styles.attendeesBtn}
                  onClick={() => navigate(`/events/attendees/${eventData.id}`)}
                >
                  View Attendees
                </button>
                <button
                  onClick={() => navigate(`/events/${eventData.id}`)}
                  className={styles.editBtn}
                >
                  Edit
                </button>
                {isE2EEvent(eventData) && (
                  <button
                    className={styles.deleteBtn}
                    onClick={() => handleRequestDelete(eventData)}
                  >
                    Delete
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {status && <div className={styles.status}>{status}</div>}

      {eventToDelete && (
        <ConfirmModal
          message={`Permanently delete "${eventToDelete.name}" and all its photos, faces, and files? This cannot be undone.`}
          onConfirm={confirmDelete}
          onCancel={() => setEventToDelete(null)}
        />
      )}
    </div>
  );
}
